import { sortTokenIds } from '@aquariusdefi/sdk';
import { Asset, StrKey, xdr } from '@stellar/stellar-sdk';
import * as StellarSdk from '@stellar/stellar-sdk';

import { ASSET_CONTRACT_METHOD, CONTRACT_STATUS } from 'constants/soroban';
import { BASE_FEE } from 'constants/stellar';
import { ACCOUNT_FOR_SIMULATE } from 'constants/stellar-accounts';

import { getNetworkPassphrase } from 'helpers/env';
import { createAsset, createLumen, getTokensFromCache } from 'helpers/token';

import Connection from 'services/soroban/connection/connection';
import { buildBatchCall, simulateBatch } from 'services/soroban/utils/batchSimulation';
import {
    contractIdToScVal,
    i128ToInt,
    publicKeyToScVal,
} from 'services/soroban/utils/scValHelpers';

import { ClassicToken, Token, TokenType } from 'types/token';

export default class TokenContract {
    tokensCache = new Map<string, Token>();
    private parseTokenPromises = new Map<string, Promise<Token>>();
    private readonly connection: Connection;

    constructor(connection: Connection) {
        this.connection = connection;

        this.restoreFromCache();
    }

    restoreFromCache() {
        const cached = getTokensFromCache();

        if (cached) {
            this.cacheTokens(cached);
        }
    }

    cacheTokens(tokens: Token[]) {
        tokens.forEach(token => {
            this.tokensCache.set(token.contract, token);
        });
    }

    getAssetContractId(asset: Asset | Token): string {
        const contract = (asset as Token).contract;

        if (contract) {
            return contract;
        }

        return (asset as Asset).contractId(getNetworkPassphrase());
    }

    parseTokenContractId(contractId: string): Promise<Token> {
        return this.parseTokenContractIds([contractId]).then(([token]) => token);
    }

    /**
     * Builds a classic token from a "CODE:ISSUER" (or "native") asset string
     * without any RPC reads. Returns null when the string doesn't match the
     * contract id (i.e. the contract is a pure Soroban token).
     */
    private tokenFromAssetString(contractId: string, assetString: string): Token | null {
        if (!assetString) {
            return null;
        }

        if (assetString === 'native') {
            if (contractId !== createLumen().contractId(getNetworkPassphrase())) {
                return null;
            }
            const lumen: ClassicToken = createLumen() as ClassicToken;
            lumen.type = TokenType.classic;
            lumen.contract = contractId;
            lumen.decimal = 7;
            return lumen;
        }

        const [code, issuer] = assetString.split(':');

        if (!code || !issuer) {
            return null;
        }

        try {
            const asset: ClassicToken = createAsset(code, issuer) as ClassicToken;

            if (asset.contractId(getNetworkPassphrase()) !== contractId) {
                return null;
            }

            asset.type = TokenType.classic;
            asset.contract = contractId;
            asset.decimal = 7;

            return asset;
        } catch {
            return null;
        }
    }

    /**
     * Resolves token metadata (name, symbol, decimals) for many contracts at
     * once. Results are cached in memory and concurrent requests for the same
     * contract are deduplicated, so repeated calls (page/filter changes) don't
     * hit the RPC again.
     */
    parseTokenContractIds(
        contractIds: string[],
        // Optional contract -> asset string ("CODE:ISSUER" / "native") hints,
        // e.g. from the backend's tokens_str. Classic tokens resolve from them
        // synchronously, keeping RPC off the critical rendering path.
        knownStrings?: Map<string, string>,
    ): Promise<Token[]> {
        if (knownStrings) {
            contractIds.forEach(contractId => {
                if (this.tokensCache.has(contractId) || !knownStrings.has(contractId)) {
                    return;
                }

                const token = this.tokenFromAssetString(contractId, knownStrings.get(contractId));

                if (token) {
                    this.tokensCache.set(contractId, token);
                }
            });
        }

        const missing = [
            ...new Set(
                contractIds.filter(
                    contractId =>
                        !this.tokensCache.has(contractId) &&
                        !this.parseTokenPromises.has(contractId) &&
                        contractId !== createLumen().contractId(getNetworkPassphrase()),
                ),
            ),
        ];

        if (missing.length) {
            const batchPromise = this.fetchTokens(missing);

            missing.forEach(contractId => {
                this.parseTokenPromises.set(
                    contractId,
                    batchPromise.then(tokens => tokens.get(contractId)),
                );
            });

            // Once settled (either way), drop the in-flight entries: successes
            // are already in tokensCache, failures should be retryable.
            batchPromise
                .catch(() => undefined)
                .then(() => {
                    missing.forEach(contractId => this.parseTokenPromises.delete(contractId));
                });
        }

        return Promise.all(
            contractIds.map(contractId => {
                if (this.tokensCache.has(contractId)) {
                    return Promise.resolve(this.tokensCache.get(contractId));
                }

                if (contractId === createLumen().contractId(getNetworkPassphrase())) {
                    const lumen: ClassicToken = createLumen() as ClassicToken;
                    lumen.type = TokenType.classic;
                    lumen.contract = contractId;
                    lumen.decimal = 7;
                    return Promise.resolve(lumen);
                }

                return this.parseTokenPromises.get(contractId);
            }),
        );
    }

    private async fetchTokens(contractIds: string[]): Promise<Map<string, Token>> {
        const calls = contractIds.flatMap(contractId => [
            buildBatchCall(contractId, ASSET_CONTRACT_METHOD.NAME, []),
            buildBatchCall(contractId, ASSET_CONTRACT_METHOD.SYMBOL, []),
            buildBatchCall(contractId, ASSET_CONTRACT_METHOD.DECIMALS, []),
        ]);

        // Metadata reads are cheap, so pack several tokens (3 calls each) into
        // one simulation; budget overflows get bisected automatically.
        const retvals = await simulateBatch(ACCOUNT_FOR_SIMULATE, calls, {
            unitSize: 3,
            maxCallsPerBatch: 30,
        });

        const tokens = new Map<string, Token>();

        contractIds.forEach((contractId, index) => {
            const [name, symbol, decimal] = retvals.slice(index * 3, index * 3 + 3).map(val => {
                const value = StellarSdk.scValToNative(val);
                // Strings that are not valid UTF-8 decode to bytes; show replacement characters.
                return value instanceof Uint8Array ? Buffer.from(value).toString() : String(value);
            });

            const [code, issuer] = name.split(':');

            let token: Token;

            try {
                const asset: ClassicToken = createAsset(code, issuer) as ClassicToken;

                if (asset.contractId(getNetworkPassphrase()) !== contractId) {
                    throw new Error();
                }

                asset.type = TokenType.classic;
                asset.contract = contractId;
                asset.decimal = 7;

                token = asset;
            } catch {
                token = {
                    type: TokenType.soroban,
                    contract: contractId,
                    name,
                    code: symbol,
                    decimal: Number(decimal),
                };
            }

            this.tokensCache.set(contractId, token);
            tokens.set(contractId, token);
        });

        return tokens;
    }

    getTokenDecimals(contractId: string): Promise<number> {
        if (this.tokensCache.has(contractId)) {
            return Promise.resolve(this.tokensCache.get(contractId).decimal);
        }
        return this.connection
            .buildSmartContractTxForSimulate(
                ACCOUNT_FOR_SIMULATE,
                contractId,
                ASSET_CONTRACT_METHOD.DECIMALS,
            )
            .then(
                tx =>
                    this.connection.simulateTx(
                        tx,
                    ) as Promise<StellarSdk.rpc.Api.SimulateTransactionSuccessResponse>,
            )
            .then(result => {
                if (!result) {
                    throw new Error();
                }
                return Number(result.result.retval.value);
            });
    }

    /**
     * Reads balances for many tokens in one batched simulation.
     * Decimals are taken from the token objects, so no extra RPC reads.
     */
    getTokenBalances(tokens: Token[], where: string): Promise<Map<string, string | null>> {
        const whereScVal = StellarSdk.StrKey.isValidEd25519PublicKey(where)
            ? publicKeyToScVal(where)
            : contractIdToScVal(where);

        const calls = tokens.map(({ contract }) =>
            buildBatchCall(contract, ASSET_CONTRACT_METHOD.GET_BALANCE, [whereScVal]),
        );

        return simulateBatch(
            ACCOUNT_FOR_SIMULATE,
            calls,
            // balance reads are cheap, pack many into one simulation
            { maxCallsPerBatch: 30 },
        ).then(
            retvals =>
                new Map(
                    tokens.map((token, index) => [
                        token.contract,
                        retvals[index] ? i128ToInt(retvals[index], token.decimal ?? 7) : null,
                    ]),
                ),
        );
    }

    async getTokenBalance(token: Asset | string, where: string) {
        const tokenContact = typeof token === 'string' ? token : this.getAssetContractId(token);
        const tokenDecimals = await this.getTokenDecimals(tokenContact);

        const tx = await this.connection.buildSmartContractTxForSimulate(
            ACCOUNT_FOR_SIMULATE,
            tokenContact,
            ASSET_CONTRACT_METHOD.GET_BALANCE,
            StellarSdk.StrKey.isValidEd25519PublicKey(where)
                ? publicKeyToScVal(where)
                : contractIdToScVal(where),
        );

        const result = (await this.connection.simulateTx(
            tx,
        )) as StellarSdk.rpc.Api.SimulateTransactionSuccessResponse;

        if (result.result) {
            return i128ToInt(result.result.retval, tokenDecimals);
        }

        return null;
    }

    deployAssetContractTx(publicKey: string, asset: Asset) {
        return this.connection
            .getAccount(publicKey)
            .then(acc => {
                const tx = new StellarSdk.TransactionBuilder(acc, {
                    fee: BASE_FEE,
                    networkPassphrase: getNetworkPassphrase(),
                });

                tx.addOperation(
                    StellarSdk.Operation.createStellarAssetContract({
                        asset,
                    }),
                );

                return tx.setTimeout(StellarSdk.TimeoutInfinite).build();
            })
            .then(tx => this.connection.prepareTransaction(tx));
    }

    getTokenContractData(
        contractId: string,
    ): Promise<{ status: CONTRACT_STATUS; ledgersBeforeExpire: number }> {
        const contractIdBuffer = StrKey.decodeContract(contractId);

        const contractKey: xdr.LedgerKey = xdr.LedgerKey.contractData(
            new xdr.LedgerKeyContractData({
                contract: StellarSdk.Address.contract(contractIdBuffer).toScAddress(),
                key: xdr.ScVal.scvLedgerKeyContractInstance(),
                durability: xdr.ContractDataDurability.persistent,
            }),
        );

        return this.connection
            .getLedgerEntries(contractKey)
            .then(({ entries, latestLedger }) => {
                if (!entries?.length) {
                    return {
                        status: CONTRACT_STATUS.NOT_FOUND,
                        ledgersBeforeExpire: 0,
                    };
                }

                const [entry] = entries;

                const contractExp = entry.liveUntilLedgerSeq;

                return {
                    status:
                        contractExp > latestLedger
                            ? CONTRACT_STATUS.ACTIVE
                            : CONTRACT_STATUS.EXPIRED,
                    ledgersBeforeExpire: Math.max(contractExp - latestLedger, 0),
                };
            })
            .catch(e => {
                console.log(e);
                return {
                    status: CONTRACT_STATUS.NOT_FOUND,
                    ledgersBeforeExpire: 0,
                };
            });
    }

    orderTokens(assets: Token[]): Token[] {
        const byId = new Map(assets.map(asset => [asset.contract, asset]));
        return sortTokenIds(assets.map(asset => asset.contract)).map(id => byId.get(id));
    }
}
